import React, { useState, useMemo } from 'react';
import { Button, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useResyncSingleRecruitmentPolicyMutation } from '../../../api/recruitmentPolicy';

export const RecruitmentPolicyMenu = ({ Component, page, row, setRuleAction }) => {
    const [actionType, setActionType] = useState();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [resyncSingleRecruitmentPolicy, { isLoading: isResyncing }] = useResyncSingleRecruitmentPolicyMutation();

    const handleResync = async () => {
        if (isResyncing) return;
        const bodyFormData = new FormData();
        bodyFormData.append('recruitmentPolicyId', row.id);
        try {
            const response = await resyncSingleRecruitmentPolicy(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, { variant: 'success' });
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        } finally {
            setActionType();
        }
    };

    const dialogContent = useMemo(() => {
        if (actionType === 'addRule') return setRuleAction('add');
        else if (actionType === 'resync')
            return {
                title: 'Resync Recruitment Policy',
                confirmation: `Are you sure you want to resync this Recruitment Policy with Positions?`,
                actions: (
                    <React.Fragment>
                        <Button size="medium" variant="text" onClick={() => setActionType()} disabled={isResyncing}>
                            No
                        </Button>
                        <Button
                            size="medium"
                            variant="contained"
                            onClick={handleResync}
                            startIcon={isResyncing ? <CircularProgress color="inherit" size="1.2rem" /> : <></>}
                        >
                            Yes
                        </Button>
                    </React.Fragment>
                ),
            };
    }, [actionType, isResyncing]);

    const actionItems = [
        page === 'list' && {
            label: 'Modify',
            type: 'modify',
            icon: <EditIcon />,
            onClick: () => navigate(`/recruitmentPolicies/${row.id}`),
            permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_PRE_CREATE_RULES_FOR_RECRUITMENT_POLICY'],
        },
        page === 'view' &&
            row?.recruitmentPolicyStatus?.toLowerCase() === 'active' && {
                label: 'Add Rule',
                type: 'addRule',
                icon: <AddIcon />,
                onClick: (action) => {
                    setActionType(action.type);
                    setRuleAction('add');
                },
                permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_SAVE_RULES_FOR_RECRUITMENT_POLICY'],
            },
        {
            label: 'Resync',
            type: 'resync',
            icon: <AutorenewIcon />,
            onClick: (action) => setActionType(action.type),
            permissions: ['ROLE_BASE_PERMISSION_RECRUIT_INDEX_RESYNC_SINGLE_RECRUITMENT_POLICY'],
        },
    ];

    return <Component actions={actionItems} dialog={dialogContent} onDialogClose={() => setActionType()} />;
};
